import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';
import DashboardTableLegend from '../../../Dashboard/detailPanel/DashboardTableLegend';
import TabsEmptyState from '../../components/TabsEmptyState';
import { ReactComponent as NoDataCardIcon } from '../../../../images/icons/facility_add_failed.svg';
import { Add as AddIcon } from '@mui/icons-material';
import CustomerCareDTable from '../../../Shared/CustomerCareDTable';
import FacilityStatusIcon from '../../components/FacilityStatusIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { GridColDef } from '@mui/x-data-grid';
import { TabProps } from '../FacilityDetails';
import NewModal from '../../../Shared/NewModal';
import AddLocationController from '../AddLocationController/AddLocationController';
import { useDispatch } from 'react-redux';
import { getParentFacilities } from '../../../../store/facility/actionCreators';
import { useNavigate } from 'react-router-dom';
import DeactivationConfirmation from '../Deactivation/DeactivationConfirmation';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import {
  customError,
  customSuccess,
  lightGrey,
  warningYellow,
} from '../../../../mainTheme';

const LocationsTab: React.FC<TabProps> = ({ locations, parentFacilityId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showDeactivation, setShowDeactivation] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  const tableLegendData = [
    {
      name: '0',
      color: customSuccess,
    },
    {
      name: '4% or less',
      color: warningYellow,
    },
    {
      name: '5% or more',
      color: customError,
    },
  ];

  const handleRowClick = (params: any) => {
    navigate(`/v2/facilities/${parentFacilityId}/location/${params.id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.7,
      sortable: true,
      sortComparator: (v1: any, v2: any) => {
        return (v1 ?? '').localeCompare(v2 ?? '');
      },
      renderCell: (params: any) => {
        return (
          <FacilityStatusIcon
            status={params.value as 'ok' | 'warning' | 'critical'}
          />
        );
      },
    },
    { field: 'name', headerName: 'Location', flex: 2 },
    {
      field: 'city',
      headerName: 'Address',
      flex: 2,
      renderCell: (params: any) => {
        const city = params.row.city || '';
        const state = params.row.state || '';
        return `${city}${city && state ? ', ' : ''}${state}`;
      },
    },
    { field: 'total_devices', headerName: 'Hubs', flex: 1 },
    {
      field: 'total_patients',
      headerName: 'Patients',
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: any) => {
        return (
          <Box
            onClick={(e) => e.stopPropagation()} // Prevent row click when clicking delete
            borderRadius="8px"
            border="1px solid var(--Outline-O5---Separator, #D4D4D9)"
          >
            <Tooltip title="Deactivate">
              <IconButton
                onClick={() => {
                  setSelectedLocationId(params.row.id);
                  setShowDeactivation(true);
                }}
                size="small"
              >
                <FontAwesomeIcon icon={faTrashCan} size="sm" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // This fn is to show loading when the modal gets success and then show the updated data
  const loadParentFacilities = async (after?: () => void) => {
    setIsLoading(true);
    await dispatch(getParentFacilities() as any);
    setIsLoading(false);
    if (after) after();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      {locations && locations.length > 0 ? (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" gap={4}>
              <DashboardTableLegend tableLegendData={tableLegendData} />
              <Typography
                sx={{
                  color: lightGrey,
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                }}
              >
                issues in the last 24 hrs
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              color="secondary"
              onClick={() => setShowAddLocation(true)}
              sx={{
                height: '40px',
              }}
            >
              Add new location
            </Button>
          </Box>
          <CustomerCareDTable
            rows={locations}
            columns={columns}
            pageSize={4}
            defaultSortField="status"
            defaultSortDirection="desc"
            hideViewAllButton
            onRowClick={handleRowClick}
          />
        </>
      ) : (
        <TabsEmptyState
          icon={<NoDataCardIcon />}
          title="There are no locations"
          description="Add a new location to get started."
          buttonText="Add new location"
          onButtonClick={() => setShowAddLocation(true)}
        />
      )}

      {showAddLocation && parentFacilityId !== undefined && (
        <NewModal
          showCloseX
          title="Add new"
          onClose={() => setShowAddLocation(false)}
          maxWidth="xs"
        >
          <AddLocationController
            parentFacilityId={parentFacilityId}
            onCancel={() => setShowAddLocation(false)}
            onBack={() => setShowAddLocation(false)}
            onSuccess={() =>
              loadParentFacilities(() => setShowAddLocation(false))
            }
          />
        </NewModal>
      )}

      {showDeactivation && selectedLocationId && (
        <DeactivationConfirmation
          type="location"
          locationId={selectedLocationId}
          onClose={() => {
            setShowDeactivation(false);
            setSelectedLocationId(null);
          }}
          onSuccess={() =>
            loadParentFacilities(() => setSelectedLocationId(null))
          }
        />
      )}
    </Box>
  );
};

export default LocationsTab;
